import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Pre-construction "By cost code" view (prototype B, 2026-10-20). The rows
// are on the page; everything a click does here happens in the browser:
// - opening a cost code's step, Previous / Next and Close swap one panel.
//   Panels are fetched on their own (GET codes/:id?step=N, a few KB), kept,
//   and fetched ahead: the neighbours of the open row and any row the mouse
//   rests on, so they open with no wait;
// - By bid package / By division re-orders the rows (both orders' headers
//   are on the page); filter chips and search hide rows;
// - after an action (carry, pick, answer…) the server answers with just the
//   rows it touched plus the open panel (Precon::CodesResponses); the whole
//   list is fetched again only when cost codes or packages changed.
// Invite everyone / Main budget / Package for owner open that whole step
// here (the prototype's "All cost codes" back bar); Edit the template opens
// step 1's editor in place of the rows.
// The page's precon controller (the parent) owns the actions inside the
// panels and hands its answers here (applyResponse / refresh / query).
export default class extends Controller {
  static targets = ["hub", "back", "backTitle", "backSub", "plans", "editBar", "full", "listCard", "progress",
                    "groupSeg", "search", "filters", "bpbar", "list", "noMatch", "tplfeat", "sortSeg"]
  static values = { baseUrl: String, projectId: Number, open: String, step: String, full: String, strings: Object }

  connect() {
    this._cache = new Map()
    this._inflight = new Map()
    this._filter = "all"
    this._query = ""
    this._openId = this.openValue || ""
    this._openStep = parseInt(this.stepValue, 10) || 0
    this._full = 0
    let saved = null
    try { saved = window.localStorage.getItem("conslog.precon.codes.group") } catch (e) { /* not remembered */ }
    if ((saved === "package" || saved === "division") && saved !== this._group) this._setGroup(saved)
    else this._showPackageBadges()
    if (this._openId) {
      const panel = this.listTarget.querySelector(".pbpanel")
      if (panel) this._cache.set(this._key(this._openId, this._openStep), panel.outerHTML)
      this._afterOpen(true)
    }
    // Rows the mouse rests on are fetched ahead (the step under the pointer,
    // else the row's own next step).
    this._over = (e) => {
      const row = e.target.closest?.("[data-code-row]")
      if (!row) return
      const btn = e.target.closest(".pbstep")
      const step = btn ? parseInt(btn.dataset.step, 10) : this._defaultStep(row)
      clearTimeout(this._hoverTimer)
      this._hoverTimer = setTimeout(() => this._prefetch(row.dataset.codeId, step), 60)
    }
    this.listCardTarget.addEventListener("pointerover", this._over)
    // Every row's panel for its next step comes in ONE answer right after
    // the page paints (GET codes/panels), so the first click on any row
    // opens with no wait; a step opened on a row brings that step for every
    // row the same way, so Previous / Next never wait either.
    this._batches = new Map()
    this._batched = new Set()
    this._shared = {}
    this._preloadTimer = setTimeout(() => this._preload("default"), 150)
    // Arrived from By step on step 3, 5 or 6: that whole step, here.
    const full = parseInt(this.fullValue, 10)
    if ([3, 5, 6].includes(full)) this.showFullStep(full, "view")
  }

  disconnect() {
    this.listCardTarget?.removeEventListener("pointerover", this._over)
    clearTimeout(this._hoverTimer)
    clearTimeout(this._preloadTimer)
  }

  // ── What the parent (precon controller) calls ─────────────────────────
  get inFull() { return this._full > 0 }

  // Sent with every action from this view: the server answers with rows.
  get query() { return `codes=${this._openId || 0}${this._openStep ? `&code_step=${this._openStep}` : ""}` }

  applyResponse(data) {
    if (this._full) {
      const html = data?.panels?.[String(this._full)]
      if (html) this.fullTarget.innerHTML = html
      else if ((data?.stale || []).map(Number).includes(this._full)) this._loadFull(this._full)
      this._listStale = true
      return
    }
    if (data?.codes) this._applyCodes(data.codes)
    else this.refresh()
  }

  async refresh(step) {
    if (this._full) {
      if (!step || step === this._full) await this._loadFull(this._full)
      this._listStale = true
      return
    }
    try {
      const data = await this._get(`${this.baseUrlValue}/panel/1?${this.query}`, "application/json")
      if (data?.codes) this._applyCodes(data.codes)
    } catch (e) { this._toast(this._s("refresh_failed"), "error") }
  }

  // For the View switch to By step (the prototype's view sync): the whole
  // step shown here, or the open cost code's step and package.
  syncParams() {
    if (this._full && this._full !== 1) return { step: this._full }
    const row = this._openId ? this._row(this._openId) : null
    if (!row || row.dataset.own) return {}
    const pkg = row.dataset.pkg
    return pkg && pkg !== "none" ? { step: this._openStep, package_id: pkg } : { step: this._openStep }
  }

  // "Open step N on package X" from a pane: the first row of that package.
  openPackageStep(packageId, step) {
    if (this._full) this.back()
    const row = this.listTarget.querySelector(`[data-code-row][data-pkg='${packageId}']`)
    if (row) this._open(row.dataset.codeId, parseInt(step, 10) || 1, true)
  }

  // ── Rows: open / close / step / previous / next ───────────────────────
  toggle(e) {
    const row = e.currentTarget.closest("[data-code-row]")
    if (!row) return
    if (row.dataset.codeId === String(this._openId)) this.close()
    else this._open(row.dataset.codeId, this._defaultStep(row), false)
  }

  openStep(e) {
    e.stopPropagation()
    const el = e.currentTarget
    const row = el.closest("[data-code-row]")
    const id = el.dataset.codeId || row?.dataset.codeId
    if (!id) return
    if (this._full) this.back()
    this._open(id, parseInt(el.dataset.step, 10) || 1, !row)
  }

  close() {
    if (!this._openId) return
    const wrap = this.listTarget.querySelector(".pbopen")
    const row = wrap?.querySelector("[data-code-row]")
    if (wrap && row) { wrap.replaceWith(row); this._paintRow(row, false, 0) }
    this._openId = ""
    this._openStep = 0
  }

  prev() { this._walk(-1) }
  next() { this._walk(1) }

  _walk(dir) {
    const rows = this._visibleRows()
    const i = rows.findIndex(r => r.dataset.codeId === String(this._openId))
    const to = rows[i + dir]
    if (!to) return
    // Stay on the same step, so you can walk every cost code through it.
    this._open(to.dataset.codeId, to.dataset.own ? 1 : (this._openStep || this._defaultStep(to)), true)
  }

  _defaultStep(row) { return row.dataset.own ? 1 : (parseInt(row.dataset.defaultStep, 10) || 1) }
  _key(id, step) { return `${id}:${step}` }

  _open(id, step, scroll) {
    const row = this._row(id)
    if (!row) return
    if (row.dataset.own) step = 1
    if (String(this._openId) === String(id) && this._openStep === step) return
    const switching = String(this._openId) !== String(id)
    if (switching) this.close()
    this._openId = String(id)
    this._openStep = step
    let wrap = row.closest(".pbopen")
    if (!wrap) {
      wrap = document.createElement("div")
      wrap.className = "pbopen"
      row.replaceWith(wrap)
      wrap.appendChild(row)
    }
    this._paintRow(row, true, step)
    const key = this._key(id, step)
    const cached = this._cache.get(key)
    this._setPanel(wrap, cached || this._placeholder())
    if (!cached) {
      this._fetchPanel(id, step).then(html => {
        if (String(this._openId) === String(id) && this._openStep === step) { this._setPanel(wrap, html); this._afterOpen(false) }
      }).catch(() => {
        if (String(this._openId) === String(id)) this._toast(this._s("load_failed"), "error")
      })
    }
    // A step other than the row's own next one: bring it for every row.
    if (step !== this._defaultStep(row)) this._preloadSoon(step)
    this._afterOpen(scroll || switching)
    track("Precon Cost Code Opened", { project_id: this.projectIdValue, step, own: !!row.dataset.own })
  }

  _setPanel(wrap, html) {
    const old = wrap.querySelector(".pbpanel")
    const tmp = document.createElement("div")
    tmp.innerHTML = html
    const panel = tmp.firstElementChild
    // A panel from the one-answer batch came without its step's pop-ups:
    // add the batch's copy inside the pane (where the pane's own would be).
    const sharedStep = panel.dataset.pbShared
    if (sharedStep && this._shared[sharedStep]) {
      const host = panel.querySelector(".pbpane > [data-controller]")
      if (host && !host.querySelector(".overlay")) host.insertAdjacentHTML("beforeend", this._shared[sharedStep])
    }
    if (old) old.replaceWith(panel); else wrap.appendChild(panel)
    this._paintWalk()
  }

  _placeholder() {
    return `<div class="pbpanel" role="region"><div class="pbbody"><div class="muted">${this._esc(this._s("loading"))}</div></div></div>`
  }

  // Row state the prototype shows when open: orange row, chevron turned,
  // the selected step ringed.
  _paintRow(row, open, step) {
    row.classList.toggle("open", open)
    const main = row.querySelector(".pbmain")
    if (main) main.setAttribute("aria-expanded", open ? "true" : "false")
    const chev = row.querySelector(".pbchev")
    if (chev) chev.classList.toggle("open", open)
    row.querySelectorAll(".pbstep").forEach(b => b.classList.toggle("sel", open && !row.dataset.own && parseInt(b.dataset.step, 10) === step))
  }

  _afterOpen(scroll) {
    this._paintWalk()
    const row = this._row(this._openId)
    if (scroll && row) setTimeout(() => row.scrollIntoView({ block: "nearest", behavior: "smooth" }), 30)
    // Fetch the neighbours on the same step ahead, so Previous / Next are instant.
    clearTimeout(this._aheadTimer)
    this._aheadTimer = setTimeout(() => {
      const rows = this._visibleRows()
      const i = rows.findIndex(r => r.dataset.codeId === String(this._openId))
      ;[rows[i + 1], rows[i - 1]].filter(Boolean).forEach(r => this._prefetch(r.dataset.codeId, r.dataset.own ? 1 : this._openStep))
    }, 120)
  }

  // Previous / Next are off at the ends of the rows shown.
  _paintWalk() {
    const rows = this._visibleRows()
    const i = rows.findIndex(r => r.dataset.codeId === String(this._openId))
    const panel = this.listTarget.querySelector(".pbopen .pbpanel")
    if (!panel) return
    const p = panel.querySelector("[data-pb-prev]")
    const n = panel.querySelector("[data-pb-next]")
    if (p) p.disabled = i <= 0
    if (n) n.disabled = i < 0 || i >= rows.length - 1
  }

  _prefetch(id, step) {
    if (!id || !step) return
    const key = this._key(id, step)
    if (this._cache.has(key) || this._inflight.has(key)) return
    this._fetchPanel(id, step).catch(() => {})
  }

  _fetchPanel(id, step) {
    const key = this._key(id, step)
    if (this._cache.has(key)) return Promise.resolve(this._cache.get(key))
    if (this._inflight.has(key)) return this._inflight.get(key)
    const gen = this._gen || 0
    // A batch on its way that carries this panel: wait for it instead of
    // asking again; fetch the one panel only if it didn't come.
    const batch = this._batches.get(String(step)) || (this._defaultStepOf(id) === step ? this._batches.get("default") : null)
    const p = (batch ? batch.then(() => (this._cache.has(key) ? this._cache.get(key) : this._fetchOne(id, step, gen))) : this._fetchOne(id, step, gen))
      .finally(() => this._inflight.delete(key))
    this._inflight.set(key, p)
    return p
  }

  _fetchOne(id, step, gen) {
    const key = this._key(id, step)
    return fetch(`${this.baseUrlValue}/codes/${id}?step=${step}`, { headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin" })
      .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.text() })
      .then(html => { if ((this._gen || 0) === gen) this._cache.set(key, html); return html })
  }

  _defaultStepOf(id) {
    const row = this._row(id)
    return row ? this._defaultStep(row) : 0
  }

  // Every row's panel for one step in one answer ("default" = each row's
  // own next step), kept like single panels. Never twice for the same rows;
  // dropped when an action came first (the rows changed under it).
  _preload(step) {
    const k = String(step)
    if (this._batched.has(k)) return Promise.resolve()
    if (this._batches.has(k)) return this._batches.get(k)
    const gen = this._gen || 0
    const p = this._get(`${this.baseUrlValue}/codes/panels?step=${encodeURIComponent(k)}`, "application/json")
      .then(data => {
        if ((this._gen || 0) !== gen) return
        // Steps 2 and 4's pop-ups come once per answer; _setPanel adds them to a panel when it opens.
        Object.assign(this._shared, data?.shared || {})
        Object.entries(data?.panels || {}).forEach(([key, html]) => { if (!this._cache.has(key)) this._cache.set(key, html) })
        this._batched.add(k)
      })
      .catch(() => { /* single panels still load on click */ })
      .finally(() => { if (this._batches.get(k) === p) this._batches.delete(k) })
    this._batches.set(k, p)
    return p
  }

  // The same, a moment later: quick clicks don't start a batch each.
  _preloadSoon(...steps) {
    clearTimeout(this._preloadTimer)
    this._preloadTimer = setTimeout(() => steps.forEach(s => this._preload(s)), 300)
  }

  // After an action every kept panel is out of date: bring them again for
  // the rows' next steps, and for the open step when it's another one.
  _refillSoon() {
    this._batched.clear()
    this._batches.clear()
    const steps = ["default"]
    if (this._openId && this._openStep && this._openStep !== this._defaultStepOf(this._openId)) steps.push(this._openStep)
    this._preloadSoon(...steps)
  }

  // ── Group, filter, search (all in the browser) ────────────────────────
  get _group() { return this.listTarget.dataset.group || "package" }

  group(e) {
    const g = e.currentTarget.dataset.group
    if (g === this._group) return
    this._setGroup(g)
    try { window.localStorage.setItem("conslog.precon.codes.group", g) } catch (err) { /* not remembered */ }
    track("Precon Cost Codes Grouped", { project_id: this.projectIdValue, group: g })
  }

  _setGroup(g) {
    const list = this.listTarget
    list.dataset.group = g
    this.groupSegTarget.querySelectorAll(".segbtn").forEach(b => { const on = b.dataset.group === g; b.classList.toggle("on", on); b.setAttribute("aria-pressed", on ? "true" : "false") })
    if (this.hasBpbarTarget) this.bpbarTarget.classList.toggle("hidden", g !== "package")
    const kind = g === "package" ? "package" : "division"
    const heads = Array.from(list.querySelectorAll(`[data-code-group][data-kind='${kind}']`)).sort((a, b) => a.dataset.ord - b.dataset.ord)
    const rows = Array.from(list.querySelectorAll("[data-code-row]:not([data-own])"))
    const by = new Map()
    rows.forEach(r => {
      const k = kind === "package" ? r.dataset.pkg : r.dataset.div
      if (!by.has(k)) by.set(k, [])
      by.get(k).push(r.closest(".pbopen") || r)
    })
    const frag = document.createDocumentFragment()
    heads.forEach(h => { frag.appendChild(h); (by.get(h.dataset.key) || []).forEach(el => frag.appendChild(el)) })
    list.querySelectorAll("[data-code-group]").forEach(h => h.classList.toggle("hidden", h.dataset.kind !== kind && h.dataset.kind !== "own"))
    const own = list.querySelector("[data-code-group][data-kind='own']")
    // Other-kind headers stay in the list (hidden) ahead of the rows.
    list.querySelectorAll(`[data-code-group]:not([data-kind='${kind}']):not([data-kind='own'])`).forEach(h => list.insertBefore(h, list.firstChild))
    if (own) list.insertBefore(frag, own); else list.appendChild(frag)
    this._showPackageBadges()
    this._applyFilter()
  }

  // The package badge on a row shows only when grouped by package.
  _showPackageBadges() {
    const on = this._group === "package"
    this.listTarget.querySelectorAll("[data-pkgno]").forEach(b => b.classList.toggle("hidden", !on))
  }

  filter(e) {
    this._filter = e.currentTarget.dataset.filter
    this._paintFilters()
    this._applyFilter()
  }

  search(e) {
    this._query = e.currentTarget.value.trim().toLowerCase()
    this._applyFilter()
  }

  clearFilter() {
    this._filter = "all"
    this._query = ""
    if (this.hasSearchTarget) this.searchTarget.value = ""
    this._paintFilters()
    this._applyFilter()
  }

  _paintFilters() {
    this.filtersTarget.querySelectorAll(".fchip").forEach(b => {
      const on = b.dataset.filter === this._filter
      b.classList.toggle("on", on)
      if (on) b.classList.remove("hidden")
      else if (b.dataset.filter !== "all") b.classList.toggle("hidden", b.dataset.count === "0")
    })
  }

  // The prototype's search: digits match the start of the code ("0754",
  // "07 54"), words match the code, name, division or package.
  _hit(row) {
    const q = this._query
    if (!q) return true
    const digits = q.replace(/[\s.\-]/g, "")
    if (/^\d+$/.test(digits)) return (row.dataset.code || "").startsWith(digits)
    return (row.dataset.search || "").includes(q)
  }

  _applyFilter() {
    const f = this._filter
    let shown = 0
    this.listTarget.querySelectorAll("[data-code-row]").forEach(row => {
      // Division 01 (yours) shows only under All, like the prototype.
      const ok = (f === "all" || (!row.dataset.own && row.dataset.filters.split(" ").includes(f))) && this._hit(row)
      const box = row.closest(".pbopen") || row
      box.classList.toggle("hidden", !ok)
      if (ok) shown++
    })
    const kind = this._group === "package" ? "package" : "division"
    this.listTarget.querySelectorAll("[data-code-group]").forEach(h => {
      if (h.dataset.kind !== kind && h.dataset.kind !== "own") { h.classList.add("hidden"); return }
      let el = h.nextElementSibling, any = false
      while (el && !el.hasAttribute("data-code-group")) {
        if (!el.classList.contains("hidden")) { any = true; break }
        el = el.nextElementSibling
      }
      h.classList.toggle("hidden", !any)
    })
    if (this.hasNoMatchTarget) this.noMatchTarget.classList.toggle("hidden", shown > 0)
    this._paintWalk()
  }

  _visibleRows() {
    return Array.from(this.listTarget.querySelectorAll("[data-code-row]")).filter(r => !(r.closest(".pbopen") || r).classList.contains("hidden"))
  }

  _row(id) { return this.listTarget.querySelector(`[data-code-row][data-code-id='${id}']`) }

  // ── Bid templates on/off and the division order (step 1's settings) ───
  askTemplates(e) {
    const box = e.currentTarget
    const turnOn = box.checked
    box.checked = !turnOn
    const k = turnOn ? "on" : "off"
    this._parent?._confirm(this._s(`tf_${k}_title`), `${this._s(`tf_${k}_text`)} ${this._s(`tf_${k}_sub`)}`, this._s(`tf_${k}_btn`), async () => {
      const data = await this._send("PATCH", "cost_codes/settings", { bid_templates_on: turnOn ? "1" : "0" })
      if (!data) return
      box.checked = turnOn
      this.tplfeatTarget.classList.toggle("on", turnOn)
      this.tplfeatTarget.title = this._s(`tf_tip_${k}`)
      this._toast(this._s(`tf_${k}_toast`))
      track("Precon Bid Templates Switched", { project_id: this.projectIdValue, on: turnOn, view: "codes" })
    })
  }

  // CSI order / Order of construction: packages are numbered again in that
  // order, so the rows come back whole.
  async sort(e) {
    const sort = e.currentTarget.dataset.sort
    if (e.currentTarget.classList.contains("on")) return
    this.sortSegTarget.querySelectorAll(".segbtn").forEach(b => { const on = b.dataset.sort === sort; b.classList.toggle("on", on); b.setAttribute("aria-pressed", on ? "true" : "false") })
    const data = await this._send("PATCH", "cost_codes/settings", { sort_build: sort === "build" ? "1" : "0" })
    if (!data) this.sortSegTarget.querySelectorAll(".segbtn").forEach(b => b.classList.toggle("on", b.dataset.sort !== sort))
    else track("Precon Cost Codes Sorted", { project_id: this.projectIdValue, sort })
  }

  // ── Ready to go (step 6) and the internal note (step 5) ───────────────
  async toggleReady(e) {
    const btn = e.currentTarget
    const on = btn.dataset.ready === "1"
    const id = this._openId
    const row = this._row(id)
    const label = row ? `${row.querySelector(".pbcode")?.textContent || ""} ${row.querySelector(".pbnm")?.textContent || ""}`.trim() : ""
    // Show it right away; the server's answer repaints the row and panel.
    const box = btn.closest(".pbready")
    if (box) { box.classList.toggle("on", on); btn.disabled = true }
    const data = await this._send("PATCH", `codes/${id}`, { ready: on ? "1" : "0" })
    if (!data) { if (box) { box.classList.toggle("on", !on); btn.disabled = false } return }
    this._toast(this._s(on ? "marked_ready" : "unmarked", { code: label }))
  }

  // The internal note is step 5's (precon_budget_notes, key code:<id>), so
  // the main budget shows the same note.
  async saveNote(e) {
    const input = e.currentTarget
    try {
      const data = await this._json("PATCH", `${this.baseUrlValue}/budget_notes`, { key: input.dataset.noteKey, body: input.value })
      const meta = input.closest(".pbbody")?.querySelector("[data-note-meta]")
      if (meta) {
        const m = typeof data.meta === "string" ? data.meta : [data.meta?.by, data.meta?.at].filter(Boolean).join(" · ")
        if (m) meta.textContent = m
        meta.classList.toggle("hidden", !input.value.trim())
      }
      this._cache.delete(this._key(this._openId, this._openStep))
      track("Precon Cost Code Note Saved", { project_id: this.projectIdValue, cleared: !input.value.trim() })
      this._toast(this._s("note_saved"))
    } catch (err) { this._toast(err.message, "error") }
  }

  // "Carry <sub> on both": that sub's bid carried on each of its packages.
  async carryAll(e) {
    const btn = e.currentTarget
    const pairs = (btn.dataset.carry || "").split(",").filter(Boolean).map(x => x.split(":"))
    if (!pairs.length) return
    btn.disabled = true
    let ok = true
    for (const [pkg, bid] of pairs) { if (!(await this._send("POST", `packages/${pkg}/carry`, { bid_id: bid }))) { ok = false; break } }
    if (!ok) { btn.disabled = false; return }
    track("Precon Bid Carried On All", { project_id: this.projectIdValue, packages: pairs.length })
    this._toast(this._s("carried_all", { sub: btn.dataset.sub, n: btn.dataset.n || "" }))
  }

  // The Answer / View button on a question opens its answer box in place.
  answer(e) {
    const row = e.currentTarget.closest("[data-question-row]")
    const form = row?.querySelector("[data-answer-form]")
    if (!form) return
    form.classList.toggle("hidden")
    if (!form.classList.contains("hidden")) form.querySelector("input")?.focus()
  }

  // ── Whole steps in this view: 3, 5, 6, and step 1's editor ───────────
  showFull(e) {
    const step = parseInt(e.currentTarget.dataset.step, 10)
    this.showFullStep(step, e.currentTarget.dataset.hub)
  }

  editTemplate() { this.showFullStep(1, "edit") }

  showFullStep(step, from) {
    this.close()
    this._full = step
    const edit = step === 1
    const t = edit ? ["", ""] : (from === "qa" ? [this._s("qa_t"), this._s("qa_s")] : [this._s(`f${step}_t`), this._s(`f${step}_s`)])
    this.hubTarget.classList.toggle("hidden", !edit)
    this.plansTarget.classList.toggle("hidden", !edit)
    this.backTarget.classList.toggle("hidden", edit)
    this.editBarTarget.classList.toggle("hidden", !edit)
    this.listCardTarget.classList.add("hidden")
    this.backTitleTarget.textContent = t[0]
    this.backSubTarget.textContent = t[1]
    this.fullTarget.dataset.step = String(step)
    this.fullTarget.style.display = "flex"
    this.fullTarget.innerHTML = `<div class="card"><div class="pad muted">${this._esc(this._s("loading"))}</div></div>`
    window.scrollTo({ top: 0 })
    this._loadFull(step).then(() => {
      // Call list: step 3's own pop-up, over the whole step.
      if (from === "calls") setTimeout(() => this.fullTarget.querySelector("[data-action*='#openCallList']")?.click(), 60)
    })
    track("Precon Whole Step Opened", { project_id: this.projectIdValue, step, view: "codes", from: from || "" })
  }

  async _loadFull(step) {
    try {
      const data = await this._get(`${this.baseUrlValue}/panel/${step}`, "application/json")
      if (this._full === step && data?.panels?.[String(step)]) this.fullTarget.innerHTML = data.panels[String(step)]
    } catch (e) { this._toast(this._s("load_failed"), "error") }
  }

  back() {
    if (!this._full) return
    this._full = 0
    this.fullTarget.style.display = "none"
    this.fullTarget.innerHTML = ""
    this.fullTarget.dataset.step = ""
    ;[this.hubTarget, this.plansTarget, this.listCardTarget].forEach(el => el.classList.remove("hidden"))
    this.backTarget.classList.add("hidden")
    this.editBarTarget.classList.add("hidden")
    // Arrived with ?step=N from By step: don't reopen that step on reload.
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.has("step") || url.searchParams.has("package_id")) {
        url.searchParams.delete("step"); url.searchParams.delete("package_id")
        history.replaceState(history.state, "", url.toString())
      }
    } catch (e) { /* keep the address as it is */ }
    // Something changed while the whole step was open: fetch the rows again.
    if (this._listStale) { this._listStale = false; this._reloadList() }
  }

  // ── Applying an action's answer ───────────────────────────────────────
  _applyCodes(c) {
    if (c.plans && this.hasPlansTarget) this.plansTarget.innerHTML = c.plans
    if (c.modal) {
      const m = this.element.querySelector("[data-precon-target='editPackagesModal']")
      if (m) {
        const tmp = document.createElement("div")
        tmp.innerHTML = c.modal
        const fresh = tmp.firstElementChild
        if (fresh && !m.classList.contains("hidden")) fresh.classList.remove("hidden")
        if (fresh) m.replaceWith(fresh)
      }
    }
    if (c.sig !== this.listTarget.dataset.sig) { this._reloadList(c.open, c.step); return }
    // Everything kept is now possibly out of date (budget share, counts).
    this._gen = (this._gen || 0) + 1
    this._cache.clear()
    Object.entries(c.rows || {}).forEach(([id, html]) => {
      const el = this._row(id)
      if (!el) return
      const tmp = document.createElement("div")
      tmp.innerHTML = html
      el.replaceWith(tmp.firstElementChild)
    })
    if (c.open && String(c.open) === String(this._openId)) {
      this._openStep = c.step || this._openStep
      this._cache.set(this._key(c.open, this._openStep), c.panel)
      const wrap = this.listTarget.querySelector(".pbopen")
      if (wrap && c.panel) this._setPanel(wrap, c.panel)
    } else if (this._openId && !c.open) {
      // The open cost code is gone (taken out of the job).
      const wrap = this.listTarget.querySelector(".pbopen")
      if (wrap) wrap.remove()
      this._openId = ""
      this._openStep = 0
    }
    Object.entries(c.hub || {}).forEach(([k, v]) => {
      const el = this.element.querySelector(`[data-hub-sub='${k}']`)
      if (el) { el.textContent = v; el.title = v }
    })
    if (c.progress && this.hasProgressTarget) this.progressTarget.textContent = c.progress
    if (c.filters) { this.filtersTarget.innerHTML = c.filters; this._paintFilters() }
    Object.entries(c.totals || {}).forEach(([k, v]) => {
      const el = this.listTarget.querySelector(`[data-amt-key='${k}']`)
      if (el) el.textContent = v
    })
    this._showPackageBadges()
    this._applyFilter()
    this._afterOpen(false)
    this._refillSoon()
  }

  // The whole list again (cost codes or packages changed).
  async _reloadList(openId = this._openId, step = this._openStep) {
    const q = new URLSearchParams({ group: this._group })
    if (openId) { q.set("code_id", openId); if (step) q.set("code_step", step) }
    try {
      const html = await this._get(`${this.baseUrlValue}/codes?${q}`, "text/html")
      const tmp = document.createElement("div")
      tmp.innerHTML = html
      const list = tmp.firstElementChild
      this.listTarget.replaceWith(list)
      this._gen = (this._gen || 0) + 1
      this._cache.clear()
      const open = list.querySelector(".pbopen [data-code-row]")
      this._openId = open ? open.dataset.codeId : ""
      this._openStep = open ? (parseInt(list.querySelector(".pbopen .pbpanel")?.dataset.step, 10) || 1) : 0
      if (open) this._cache.set(this._key(this._openId, this._openStep), list.querySelector(".pbopen .pbpanel").outerHTML)
      this._setGroup(this._group)
      this._afterOpen(false)
      this._refillSoon()
    } catch (e) { this._toast(this._s("refresh_failed"), "error") }
  }

  // ── Helpers ───────────────────────────────────────────────────────────
  get _parent() {
    const el = this.element.closest("[data-controller~='precon']")
    return el ? this.application.getControllerForElementAndIdentifier(el, "precon") : null
  }

  async _send(method, path, body) {
    try {
      const data = await this._json(method, `${this.baseUrlValue}/${path}?${this.query}`, body)
      if (data?.summary) this._parent?._applySummary?.(data.summary)
      if (data?.codes) this._applyCodes(data.codes)
      return data
    } catch (e) {
      this._toast(e.message, "error")
      return null
    }
  }

  async _json(method, url, body) {
    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "" },
      credentials: "same-origin",
      body: body === undefined ? undefined : JSON.stringify(body)
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || data.ok === false) throw new Error(data.error || `${res.status}`)
    return data
  }

  async _get(url, accept) {
    const res = await fetch(url, { headers: { Accept: accept, "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin" })
    if (!res.ok) throw new Error(String(res.status))
    return accept === "text/html" ? res.text() : res.json()
  }

  _toast(message, type = "notice") {
    const p = this._parent
    if (p?._showToast) p._showToast(message, type)
  }

  _s(key, vars = {}) {
    let s = (this.stringsValue || {})[key] || key
    Object.entries(vars).forEach(([k, v]) => { s = s.replaceAll(`%{${k}}`, v) })
    return s
  }

  _esc(s) { return String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }
}
